import {
  CreateGHADiscountRuleBody,
  CreateGHADiscountRuleDiscountType,
  GHADiscountRule,
  GHADiscountRuleAccountType,
  GHADiscountRuleDiscountType,
} from '~/services/ConnectionGoogleService';
import { formatDateWithClock } from '~/services/TimeService';

import { GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS } from './constants';

export type GHADiscountRuleMode = 'universal' | 'country';

export type GHADiscountCountryDiscountEntry = {
  countryCode: string;
  type: CreateGHADiscountRuleDiscountType;
  value: string;
};

type CreateGHADiscountRuleCountryDiscounts = NonNullable<CreateGHADiscountRuleBody['countryDiscounts']>;

export type GHADiscountRuleFormState = {
  propertyId: string;
  accountType?: GHADiscountRuleAccountType;
  mode: GHADiscountRuleMode;
  discountType: CreateGHADiscountRuleDiscountType;
  discountValue: string;
  countryDiscounts: Array<GHADiscountCountryDiscountEntry>;
  minAdvanceBookingWindow: string;
  minLengthOfStay: string;
};

export function toCreateGHADiscountRuleBody(
  form: GHADiscountRuleFormState & { accountType: GHADiscountRuleAccountType },
): CreateGHADiscountRuleBody {
  const body: CreateGHADiscountRuleBody = {
    propertyId: form.propertyId.trim(),
    accountType: form.accountType,
    minAdvanceBookingWindow: Number(form.minAdvanceBookingWindow),
    minLengthOfStay: Number(form.minLengthOfStay),
  };

  if (form.mode === 'country') {
    body.countryDiscounts = form.countryDiscounts.reduce<CreateGHADiscountRuleCountryDiscounts>((acc, entry) => {
      if (isCountryDiscountEntryComplete(entry)) {
        acc[normalizeCountryCode(entry.countryCode)] = {
          type: entry.type,
          value: Number(entry.value),
        };
      }

      return acc;
    }, {});

    return body;
  }

  body.discountType = form.discountType;
  body.discountValue = Number(form.discountValue);

  return body;
}

export function formatDiscountAccountType(value: string) {
  return GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function formatDiscountType(value: GHADiscountRuleDiscountType | null) {
  switch (value) {
    case 'percentage':
      return 'Percentage';
    case 'margin_retention':
      return 'Margin retention';
    case null:
      return 'Country-specific';
    default:
      return value;
  }
}

function formatSingleDiscount({
  type,
  value,
}: {
  type: CreateGHADiscountRuleDiscountType | GHADiscountRuleDiscountType;
  value: number | null;
}) {
  if (!type || value === null) {
    return 'Country-specific';
  }

  if (type === 'margin_retention') {
    return `Retain ${value}% margin`;
  }
  return `${value}% off`;
}

export function formatCountryDiscounts(countryDiscounts?: GHADiscountRule['countryDiscounts'] | null) {
  if (!countryDiscounts || Object.keys(countryDiscounts).length === 0) {
    return 'Country-specific';
  }

  return Object.entries(countryDiscounts)
    .sort(([countryCodeA], [countryCodeB]) => countryCodeA.localeCompare(countryCodeB))
    .map(([countryCode, discount]) => `${countryCode}: ${formatSingleDiscount(discount)}`)
    .join(', ');
}

export function formatRuleDiscount(rule: GHADiscountRule) {
  if (rule.countryDiscounts && Object.keys(rule.countryDiscounts).length > 0) {
    return formatCountryDiscounts(rule.countryDiscounts);
  }

  return formatSingleDiscount({ type: rule.discountType, value: rule.discountValue });
}

function normalizeCountryCode(value: string) {
  return value.trim().toUpperCase();
}

export function isValidCountryCode(value: string) {
  return /^[A-Z]{2}$/.test(value);
}

export function getDuplicateCountryCodes(entries: Array<GHADiscountCountryDiscountEntry>) {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  entries.forEach((entry) => {
    const countryCode = normalizeCountryCode(entry.countryCode);

    if (!countryCode) {
      return;
    }

    if (seen.has(countryCode)) {
      duplicates.add(countryCode);
      return;
    }

    seen.add(countryCode);
  });

  return Array.from(duplicates).sort();
}

export function isCountryDiscountEntryComplete(entry: GHADiscountCountryDiscountEntry) {
  return Boolean(entry.countryCode.trim() && entry.value !== '');
}

export function formatDateTime(value?: string | null) {
  return value ? formatDateWithClock(value) : '-';
}
